import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/common/BreadCrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import {
  Badge,
  ExportButton,
  TooltipDetail,
} from "../../components/micro/input";
import { SortDownArrow } from "../../assets/svg";
import CustomTable from "../../components/customTable/CustomTable";
import { getActivityList } from "../../redux/staffs/slice";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/loader/RippleLoader";

const ActivityLog = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");

  // selector
  const { activityList, activityListLoading } = useSelector((store) => ({
    activityList: store?.staffsData?.getActivityListStatus?.data,
    activityListLoading: store?.staffsData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getActivityList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  const handleSalesExportClick = () => {
    if (!activityList?.data?.data || activityList?.data?.data.length === 0) {
      alert("No data available to export!");
      return;
    }

    const header = [
      "ID",
      "User",
      "User Role",
      "Method",
      "URI",
      "IP",
      "Input",
      "Timestamp",
    ];

    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '""';
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    };

    const rows = activityList?.data?.data?.map((sale) => [
      escapeCSV(sale?.id || "-"),
      escapeCSV(sale?.name || "-"),
      escapeCSV(sale?.user_role || "-"),
      escapeCSV(sale?.method || "-"),
      escapeCSV(sale?.url || "-"),
      escapeCSV(sale?.ip_address || "-"),
      escapeCSV(sale?.input || "-"),
      escapeCSV(sale?.timestamp || "-"),
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "logs_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start w-24"
      >
        <div className="flex items-center">
          <span>ID.</span>
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>User</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "name" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("user_role");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>User Role</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "user_role"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("method");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Method</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "method" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        URI
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        IP
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        Input
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("timestamp");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Time Stamp</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "timestamp"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
    </>
  );

  const DataRows = () => (
    <>
      {activityList?.data?.data?.map((element, index) => (
        <tr
          key={index}
          className="text-sm shadow-sm text-mgrey hover:bg-lightprimaryorange font-Proxima"
        >
          <td className="px-5 py-3">{element.id}</td>
          <td className="px-5 py-3">{element.name}</td>
          <td className="px-5 py-3">{element.user_role}</td>

          <td className="px-5 py-3">
            <div className="flex justify-start ">
              <Badge
                className={`capitalize whitespace-nowrap ${
                  element.method === "POST"
                    ? "border-borderGreen text-bgGreen !text-xs"
                    : element.method === "GET"
                    ? "border-themeBtn text-themeBtn"
                    : element.method === "PUT"
                    ? "border-blue-800 text-blue-800"
                    : element.method === "DELETE"
                    ? "border-red text-red"
                    : ""
                }`}
                value={
                  element.method === "POST"
                    ? "POST"
                    : element.method === "GET"
                    ? "GET"
                    : element.method === "PUT"
                    ? "PUT"
                    : element.method === "DELETE"
                    ? "DELETE"
                    : ""
                }
              />
            </div>
          </td>
          <td className="px-5 py-3">{element.url}</td>
          <td className="px-5 py-3">{element.ip_address}</td>
          <td className="px-5 py-3 max-w-xs">
            <TooltipDetail
              tooltipLabel={
                <div className="whitespace-normal break-words max-w-xs">
                  {element.input}
                </div>
              }
              isLeft={true}
            >
              <div className="truncate max-w-full overflow-hidden text-ellipsis cursor-default w-full">
                {element.input}
              </div>
            </TooltipDetail>
          </td>

          <td className="px-5 py-3 ">{element.timestamp}</td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5">
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Activities Log
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Activities Log"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
        </div>
        <div className="flex justify-between items-center space-x-3">
          <DebounceSearch
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <ExportButton value="EXPORT CSV" onClick={handleSalesExportClick} />
        </div>
        {activityListLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg mt-5 ">
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={activityList?.data}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
              total={activityList?.data?.total}
              lastPage={Math.ceil(activityList?.data?.total / pageLimit)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ActivityLog;
